import React, { useState, useEffect, Fragment } from 'react';
import { useBranch } from '../../../layouts/BranchContext'; // fix path
import {
  Button,
  Container,
  FormControl,
  InputLabel,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Select,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Modal,
  Tooltip,
  useTheme,
  CircularProgress,
  Chip,
  InputAdornment,
  ListSubheader,
  Badge,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import { usePdfSettings } from '../../../layouts/PdfSettingsContext.js';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// ============================================================
// COUNTRY CODES
// ============================================================

const COUNTRY_CODES = [
  { code: '+91', country: 'IN', flag: '🇮🇳' },
  { code: '+1', country: 'US', flag: '🇺🇸' },
  { code: '+44', country: 'GB', flag: '🇬🇧' },
  { code: '+61', country: 'AU', flag: '🇦🇺' },
  { code: '+971', country: 'AE', flag: '🇦🇪' },
  { code: '+65', country: 'SG', flag: '🇸🇬' },
  { code: '+60', country: 'MY', flag: '🇲🇾' },
  { code: '+966', country: 'SA', flag: '🇸🇦' },
  { code: '+974', country: 'QA', flag: '🇶🇦' },
  { code: '+968', country: 'OM', flag: '🇴🇲' },
  { code: '+973', country: 'BH', flag: '🇧🇭' },
  { code: '+965', country: 'KW', flag: '🇰🇼' },
  { code: '+64', country: 'NZ', flag: '🇳🇿' },
  { code: '+1', country: 'CA', flag: '🇨🇦' },
  { code: '+49', country: 'DE', flag: '🇩🇪' },
  { code: '+33', country: 'FR', flag: '🇫🇷' },
  { code: '+81', country: 'JP', flag: '🇯🇵' },
  { code: '+86', country: 'CN', flag: '🇨🇳' },
  { code: '+92', country: 'PK', flag: '🇵🇰' },
  { code: '+880', country: 'BD', flag: '🇧🇩' },
  { code: '+94', country: 'LK', flag: '🇱🇰' },
  { code: '+977', country: 'NP', flag: '🇳🇵' },
  { code: '+27', country: 'ZA', flag: '🇿🇦' },
  { code: '+234', country: 'NG', flag: '🇳🇬' },
  { code: '+254', country: 'KE', flag: '🇰🇪' },
];

// ============================================================
// HELPERS
// ============================================================

const getCourseName = (courseId, courseData) => {
  const course = courseData.find(
    (c) => c.id === courseId
  );

  return (
    course?.name ||
    course?.course_name ||
    `Course ${courseId}`
  );
};

const getBatchesGroupedByCourse = (
  batches,
  selectedCourses,
  courseData
) => {
  if (selectedCourses.length === 0) {
    return {};
  }

  const grouped = {};

  const filteredBatches = batches.filter((batch) =>
    selectedCourses.includes(batch.course)
  );

  filteredBatches.forEach((batch) => {
    const courseName = getCourseName(
      batch.course,
      courseData
    );

    if (!grouped[courseName]) {
      grouped[courseName] = [];
    }

    grouped[courseName].push(batch);
  });

  return grouped;
};

// ============================================================
// COMPONENT
// ============================================================

const AddEnquiry = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { pdfThemeColor, pdfTextColor } = usePdfSettings();

  // ==========================================================
  // STATES
  // ==========================================================

  const [showSuccessModal, setShowSuccessModal] =
    useState(false);

  const [openSnackbar, setOpenSnackbar] =
    useState(false);

  const [snackbarMessage, setSnackbarMessage] =
    useState('');

  const [snackbarSeverity, setSnackbarSeverity] =
    useState('success');

  const [isSubmitting, setIsSubmitting] =
    useState(false);

  const [isLoading, setIsLoading] =
    useState(false);

      const { selectedBranchId } = useBranch();

  const [organizationId] =
    useState(() => {
      try {
        const sessionUser = JSON.parse(
          sessionStorage.getItem('user') || '{}'
        );

        return (
          sessionUser.organization_id || ''
        );
      } catch (e) {
        return '';
      }
    });

  // ==========================================================
  // COUNTRY CODE STATES
  // ==========================================================

  const [mobileCountryCode, setMobileCountryCode] =
    useState('+91');

  const [altMobileCountryCode, setAltMobileCountryCode] =
    useState('+91');

  // ==========================================================
  // DROPDOWN STATES
  // ==========================================================

  const [courseOpen, setCourseOpen] =
    useState(false);

  const [batchOpen, setBatchOpen] =
    useState(false);

  const [tempCourse, setTempCourse] =
    useState([]);

  const [tempBatch, setTempBatch] =
    useState([]);

  const [filteredBatches, setFilteredBatches] =
    useState([]);

  const [demoCourseOpen, setDemoCourseOpen] =
    useState(false);

  const [demoBatchOpen, setDemoBatchOpen] =
    useState(false);

  // ==========================================================
  // FORM DATA
  // IMPORTANT:
  // preferredTime = 10:00
  // ==========================================================

  const getInitialFormData = () => ({
    sendMail: false,
    sendTextSMS: false,
    corporate: false,

    enquiryDate:
      new Date()
        .toISOString()
        .split('T')[0],

    clientName: '',
    aadharCardNo: '',
    mobileNo: '',
    alternateMobileNo: '',
    email: '',
    address: '',

    nextFollowUp:
      new Date(
        Date.now() + 7 * 24 * 60 * 60 * 1000
      )
        .toISOString()
        .split('T')[0],

    // ✅ FIX: Default 10 AM
    preferredTime: '10:00',

    followUpMedium: '',
    enquirySource: '',
    assignedTo: '',
    status: '',
    nationality: '',
    remark: '',

    course: [],
    batch: [],

    referName: '',
    referEmail: '',
    referPhone: '',

    anyPersonToEnroll: false,

    scheduleDemoFlag: false,

    demoCourse: [],
    demoBatch: [],
    demoFaculty: [],
    demoDate: '',
    demoTime: '',
  });

  const [formData, setFormData] =
    useState(getInitialFormData);

  // ==========================================================
  // API DATA
  // ==========================================================

  const [followUpMediumData, setFollowUpMediumData] =
    useState([]);

  const [enquirySourceData, setEnquirySourceData] =
    useState([]);

  const [assignedToData, setAssignedToData] =
    useState([]);

  const [nationalityData, setNationalityData] =
    useState([]);

  const [courseData, setCourseData] =
    useState([]);

  const [batchData, setBatchData] =
    useState([]);

  const [facultyData, setFacultyData] =
    useState([]);

  const [designations, setDesignations] =
    useState([]);

  const [
    counselorDesignationIds,
    setCounselorDesignationIds,
  ] = useState([]);

  const [statusData, setStatusData] =
    useState([]);

  const [showDemoModal, setShowDemoModal] =
    useState(false);

  const [tempDemoCourse, setTempDemoCourse] =
    useState([]);

  const [tempDemoBatch, setTempDemoBatch] =
    useState([]);

  const [filteredDemoBatches, setFilteredDemoBatches] =
    useState([]);

  const [
    filteredDemoFaculty,
    setFilteredDemoFaculty,
  ] = useState([]);

  // ==========================================================
  // FILTER MAIN BATCHES
  // ==========================================================

  useEffect(() => {
    if (formData.course.length === 0) {
      setFilteredBatches([]);

      setFormData((prev) => ({
        ...prev,
        batch: [],
      }));

      return;
    }

    const filtered = batchData.filter(
      (batch) =>
        formData.course.includes(batch.course)
    );

    setFilteredBatches(filtered);

    const validSelectedBatches =
      formData.batch.filter((batchId) =>
        filtered.some(
          (b) =>
            b.id === parseInt(batchId)
        )
      );

    if (
      validSelectedBatches.length !==
      formData.batch.length
    ) {
      setFormData((prev) => ({
        ...prev,
        batch: validSelectedBatches,
      }));
    }
  }, [formData.course, batchData]);

  // ==========================================================
  // FETCH ALL DROPDOWN DATA
  // ==========================================================

    useEffect(() => {
    if (!selectedBranchId) {
      return;
    }

    const sessionUser =
      JSON.parse(
        sessionStorage.getItem('user') || '{}'
      );

    const orgId =
      String(sessionUser.organization_id || '');

    if (!orgId) {
      return;
    }

    fetchAllDropdownData(
      selectedBranchId,
      orgId
    );
  }, [selectedBranchId]);

  // ==========================================================
  // FETCH DROPDOWNS
  // ==========================================================

   const fetchAllDropdownData = async (
    branchId = selectedBranchId,
    orgId = organizationId
  ) => {
    if (!branchId || !orgId) {
      console.warn(
        'fetchAllDropdownData skipped',
        {
          branchId,
          orgId,
        }
      );

      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const accessToken =
      localStorage.getItem('accessToken');

    try {
      // ======================================================
      // 1. FETCH DESIGNATIONS
      // ======================================================

      const designationsRes =
        await fetch(
          `${API_BASE_URL}/api/master/designations/`,
          {
            headers: {
              Authorization:
                `Bearer ${accessToken}`,
            },
          }
        );

      let counselorIds = [];

      if (designationsRes.ok) {
        const designationsData =
          await designationsRes.json();

        console.log(
          'Designations data:',
          designationsData
        );

        let designationsList = [];

        if (
          designationsData &&
          Array.isArray(
            designationsData.data
          )
        ) {
          designationsList =
            designationsData.data;
        } else if (
          Array.isArray(
            designationsData
          )
        ) {
          designationsList =
            designationsData;
        } else if (
          Array.isArray(
            designationsData.results
          )
        ) {
          designationsList =
            designationsData.results;
        }

        setDesignations(
          designationsList
        );

        // ====================================================
        // ✅ FIND ACTUAL COUNSELOR DESIGNATION IDS
        // ====================================================

        const counselorDesignations =
          designationsList.filter(
            (designation) => {
              const name = (
                designation.name ||
                designation.designation_name ||
                ''
              )
                .toLowerCase()
                .trim();

              return (
                name === 'counselor' ||
                name === 'counsellor' ||
                name === 'counselors' ||
                name === 'counsellors'
              );
            }
          );

        counselorIds =
          counselorDesignations
            .map(
              (designation) =>
                designation.id ||
                designation.designationId
            )
            .filter(
              (id) =>
                id !== null &&
                id !== undefined
            );

        setCounselorDesignationIds(
          counselorIds
        );

        console.log(
          '✅ Counselor designation IDs:',
          counselorIds
        );
      }

      // ======================================================
      // 2. FETCH COMMON DROPDOWN DATA
      // ======================================================

      const [
        followUpMediumRes,
        enquirySourceRes,
        nationalityRes,
        courseRes,
        batchRes,
        facultyRes,
        statusRes,
      ] = await Promise.all([
        fetch(
          `${API_BASE_URL}/api/master/followup-mediums/`,
          {
            headers: {
              Authorization:
                `Bearer ${accessToken}`,
            },
          }
        ),

        fetch(
          `${API_BASE_URL}/api/master/source/`,
          {
            headers: {
              Authorization:
                `Bearer ${accessToken}`,
            },
          }
        ),

        fetch(
          `${API_BASE_URL}/api/master/nationalities/`,
          {
            headers: {
              Authorization:
                `Bearer ${accessToken}`,
            },
          }
        ),

        fetch(
          `${API_BASE_URL}/api/course/courses/?organization=${orgId}&branch=${branchId}`,
          {
            headers: {
              Authorization:
                `Bearer ${accessToken}`,
            },
          }
        ),

        fetch(
          `${API_BASE_URL}/api/course/batches/`,
          {
            headers: {
              Authorization:
                `Bearer ${accessToken}`,
            },
          }
        ),

        // ✅ Faculty / Trainers
        fetch(
          `${API_BASE_URL}/api/staff/employees/?organization_id=${orgId}&branch_id=${branchId}`,
          {
            headers: {
              Authorization:
                `Bearer ${accessToken}`,
            },
          }
        ),

        fetch(
          `${API_BASE_URL}/api/master/status/`,
          {
            headers: {
              Authorization:
                `Bearer ${accessToken}`,
            },
          }
        ),
      ]);

      const followUpMediumDataJson =
        await followUpMediumRes.json();

      const enquirySourceDataJson =
        await enquirySourceRes.json();

      const nationalityDataJson =
        await nationalityRes.json();

      const courseDataJson =
        await courseRes.json();

      const batchDataJson =
        await batchRes.json();

      const facultyDataJson =
        await facultyRes.json();

      const statusDataJson =
        await statusRes.json();

      // ======================================================
      // NORMALIZE COMMON DATA
      // ======================================================

      setFollowUpMediumData(
        followUpMediumDataJson.data ||
          followUpMediumDataJson.results ||
          followUpMediumDataJson ||
          []
      );

      setEnquirySourceData(
        enquirySourceDataJson.data ||
          enquirySourceDataJson.results ||
          enquirySourceDataJson ||
          []
      );

      setNationalityData(
        nationalityDataJson.data ||
          nationalityDataJson.results ||
          nationalityDataJson ||
          []
      );

      setCourseData(
        courseDataJson.results ||
          courseDataJson.data ||
          (
            Array.isArray(courseDataJson)
              ? courseDataJson
              : []
          )
      );

      setBatchData(
        batchDataJson.data ||
          batchDataJson.results ||
          batchDataJson ||
          []
      );

      const normalizedStatusData =
        statusDataJson.data ||
        statusDataJson.results ||
        (Array.isArray(statusDataJson)
          ? statusDataJson
          : []);

      setStatusData(normalizedStatusData);

      // Default the Add Enquiry status to "Admitted" once
      // the status options have been loaded from the API.
      const admittedStatus = normalizedStatusData.find((status) => {
        const statusName = String(
          status.name ||
          status.status_name ||
          status.status ||
          ''
        )
          .trim()
          .toLowerCase();

        return statusName === 'admitted';
      });

      if (admittedStatus?.id !== undefined && admittedStatus?.id !== null) {
        setFormData((prev) => ({
          ...prev,
          status:
            prev.status === '' || prev.status === null || prev.status === undefined
              ? String(admittedStatus.id)
              : prev.status,
        }));
      }

      // ======================================================
      // FACULTY / TRAINER
      // ======================================================

      let facultyList = [];

      if (
        facultyDataJson &&
        Array.isArray(
          facultyDataJson.data
        )
      ) {
        facultyList =
          facultyDataJson.data;
      } else if (
        facultyDataJson &&
        Array.isArray(
          facultyDataJson.results
        )
      ) {
        facultyList =
          facultyDataJson.results;
      } else if (
        Array.isArray(
          facultyDataJson
        )
      ) {
        facultyList =
          facultyDataJson;
      }

      const trainers =
        facultyList.filter(
          (employee) => {
            const designation = (
              employee.designation_name ||
              employee.designation ||
              ''
            )
              .toLowerCase()
              .trim();

            return designation.includes(
              'trainer'
            );
          }
        );

      setFacultyData(trainers);

      // ======================================================
      // ✅ ASSIGNED TO = COUNSELORS
      // ======================================================

      let counselors = [];

      if (
        counselorIds.length > 0
      ) {
        const counselorResponses =
          await Promise.all(
            counselorIds.map(
              async (designationId) => {
                try {
                  const response =
                    await fetch(
                      `${API_BASE_URL}/api/staff/employees/filter/?organization_id=${orgId}&branch_id=${branchId}&designation_id=${designationId}`,
                      {
                        headers: {
                          Authorization:
                            `Bearer ${accessToken}`,
                        },
                      }
                    );

                  if (!response.ok) {
                    console.warn(
                      `Counselor API failed for designation ${designationId}:`,
                      response.status
                    );

                    return [];
                  }

                  const data =
                    await response.json();

                  if (
                    Array.isArray(data)
                  ) {
                    return data;
                  }

                  if (
                    Array.isArray(
                      data?.data
                    )
                  ) {
                    return data.data;
                  }

                  if (
                    Array.isArray(
                      data?.results
                    )
                  ) {
                    return data.results;
                  }

                  return [];
                } catch (error) {
                  console.error(
                    `Error fetching counselor designation ${designationId}:`,
                    error
                  );

                  return [];
                }
              }
            )
          );

        counselors =
          counselorResponses.flat();
      }

      // ======================================================
      // UNIQUE COUNSELORS
      // ======================================================

      const uniqueCounselors =
        counselors.filter(
          (counselor, index, self) =>
            index ===
            self.findIndex(
              (item) =>
                String(
                  item.id ||
                  item.employeeId
                ) ===
                String(
                  counselor.id ||
                  counselor.employeeId
                )
            )
        );

      console.log(
        '✅ Assigned To counselors:',
        uniqueCounselors
      );

      setAssignedToData(
        uniqueCounselors
      );
    } catch (error) {
      console.error(
        '❌ Error fetching dropdown data:',
        error
      );

      setSnackbarMessage(
        'Failed to load dropdown data. Please refresh the page.'
      );

      setSnackbarSeverity(
        'error'
      );

      setOpenSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  // ==========================================================
  // FILTER DEMO BATCHES
  // ==========================================================

  useEffect(() => {
    if (
      tempDemoCourse.length === 0
    ) {
      setFilteredDemoBatches([]);

      setTempDemoBatch([]);

      return;
    }

    const filtered =
      batchData.filter(
        (batch) =>
          tempDemoCourse.includes(
            batch.course
          )
      );

    setFilteredDemoBatches(
      filtered
    );

    const validSelectedBatches =
      tempDemoBatch.filter(
        (batchId) =>
          filtered.some(
            (b) =>
              b.id ===
              parseInt(batchId)
          )
      );

    if (
      validSelectedBatches.length !==
      tempDemoBatch.length
    ) {
      setTempDemoBatch(
        validSelectedBatches
      );
    }
  }, [
    tempDemoCourse,
    batchData,
  ]);

  // ==========================================================
  // FILTER DEMO FACULTY
  // ==========================================================

  useEffect(() => {
    if (
      tempDemoCourse.length === 0 ||
      tempDemoBatch.length === 0
    ) {
      setFilteredDemoFaculty([]);

      return;
    }

    const facultyIds =
      new Set();

    // --------------------------------------------------------
    // BATCH FACULTY
    // --------------------------------------------------------

    tempDemoBatch.forEach(
      (batchId) => {
        const batch =
          batchData.find(
            (b) =>
              b.id ===
              parseInt(batchId)
          );

        if (!batch) {
          return;
        }

        [
          batch.trainer,
          batch.faculty,
          batch.trainer_id,
          batch.faculty_id,
          batch.trainer_details?.id,
          batch.faculty_details?.id,
        ]
          .filter(
            (id) =>
              id !== undefined &&
              id !== null
          )
          .forEach(
            (id) =>
              facultyIds.add(
                parseInt(id)
              )
          );

        const arrayFields = [
          batch.trainers,
          batch.faculties,
          batch.assigned_faculty,
        ];

        arrayFields.forEach(
          (arr) => {
            if (!Array.isArray(arr)) {
              return;
            }

            arr.forEach(
              (item) => {
                const id =
                  typeof item ===
                  'object'
                    ? item?.id
                    : item;

                if (
                  id !== undefined &&
                  id !== null
                ) {
                  facultyIds.add(
                    parseInt(id)
                  );
                }
              }
            );
          }
        );
      }
    );

    // --------------------------------------------------------
    // COURSE FACULTY FALLBACK
    // --------------------------------------------------------

    if (facultyIds.size === 0) {
      tempDemoCourse.forEach(
        (courseId) => {
          const course =
            courseData.find(
              (c) =>
                c.id ===
                parseInt(courseId)
            );

          if (!course) {
            return;
          }

          [
            course.trainer,
            course.faculty,
            course.trainer_id,
            course.faculty_id,
            course.trainer_details?.id,
            course.faculty_details?.id,
          ]
            .filter(
              (id) =>
                id !== undefined &&
                id !== null
            )
            .forEach(
              (id) =>
                facultyIds.add(
                  parseInt(id)
                )
            );
        }
      );
    }

    // --------------------------------------------------------
    // MATCH FACULTY
    // --------------------------------------------------------

    if (facultyIds.size === 0) {
      setFilteredDemoFaculty(
        facultyData
      );

      return;
    }

    const matched =
      facultyData.filter(
        (faculty) =>
          facultyIds.has(
            faculty.id
          )
      );

    if (
      matched.length === 0
    ) {
      setFilteredDemoFaculty(
        facultyData
      );
    } else {
      setFilteredDemoFaculty(
        matched
      );
    }

    setFormData((prev) => {
      if (
        !prev.demoFaculty ||
        prev.demoFaculty.length === 0
      ) {
        return prev;
      }

      const validFaculty =
        prev.demoFaculty.filter(
          (facultyId) =>
            matched.some(
              (faculty) =>
                faculty.id ===
                parseInt(
                  facultyId
                )
            )
        );

      return validFaculty.length ===
        prev.demoFaculty.length
        ? prev
        : {
            ...prev,
            demoFaculty:
              validFaculty,
          };
    });
  }, [
    tempDemoCourse,
    tempDemoBatch,
    batchData,
    courseData,
    facultyData,
  ]);

  // ==========================================================
  // ANY PERSON
  // ==========================================================

  const handleAnyPersonChange =
    (e) => {
      const value =
        e.target.value ===
        'yes';

      setFormData((prev) => ({
        ...prev,

        anyPersonToEnroll:
          value,

        referName:
          value
            ? prev.referName
            : '',

        referEmail:
          value
            ? prev.referEmail
            : '',

        referPhone:
          value
            ? prev.referPhone
            : '',
      }));
    };

  // ==========================================================
  // DEMO CHECKBOX
  // ==========================================================

  const handleDemoCheckboxChange =
    (e) => {
      const checked =
        e.target.checked;

      setFormData((prev) => ({
        ...prev,
        scheduleDemoFlag:
          checked,
      }));

      if (checked) {
        setTempDemoCourse([
          ...(
            Array.isArray(
              formData.demoCourse
            )
              ? formData.demoCourse
              : []
          ),
        ]);

        setTempDemoBatch([
          ...(
            Array.isArray(
              formData.demoBatch
            )
              ? formData.demoBatch
              : []
          ),
        ]);

        setShowDemoModal(true);
      } else {
        setFormData((prev) => ({
          ...prev,
          demoCourse: [],
          demoBatch: [],
          demoFaculty: [],
        }));
      }
    };

  // ==========================================================
  // DEMO GROUPING
  // ==========================================================

  const getDemoBatchesGroupedByCourse =
    () => {
      if (
        tempDemoCourse.length === 0
      ) {
        return {};
      }

      const grouped = {};

      const filtered =
        batchData.filter(
          (batch) =>
            tempDemoCourse.includes(
              batch.course
            )
        );

      filtered.forEach(
        (batch) => {
          const courseName =
            getCourseName(
              batch.course,
              courseData
            );

          if (
            !grouped[courseName]
          ) {
            grouped[courseName] =
              [];
          }

          grouped[
            courseName
          ].push(batch);
        }
      );

      return grouped;
    };

  // ==========================================================
  // SAVE DEMO DETAILS
  // ==========================================================

  const handleSaveDemoDetails =
    () => {
      setFormData((prev) => ({
        ...prev,
        demoCourse:
          tempDemoCourse,
        demoBatch:
          tempDemoBatch,
      }));

      setShowDemoModal(
        false
      );
    };

  // ==========================================================
  // MAIN CHANGE HANDLER
  // ==========================================================

  const handleChange = (e) => {
    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    // --------------------------------------------------------
    // MAIN COURSE
    // --------------------------------------------------------

    if (name === 'course') {
      const selectedCourses =
        typeof value === 'string'
          ? value.split(',')
          : value;

      setFormData(
        (prevData) => ({
          ...prevData,

          course:
            selectedCourses,

          demoCourse:
            selectedCourses.length >
            0
              ? [
                  selectedCourses[0],
                ]
              : [],
        })
      );

      return;
    }

    // --------------------------------------------------------
    // MAIN MOBILE
    // --------------------------------------------------------

    if (
      name === 'mobileNo' ||
      name ===
        'alternateMobileNo'
    ) {
      const digitsOnly =
        value
          .replace(/\D/g, '')
          .slice(0, 10);

      setFormData(
        (prevData) => ({
          ...prevData,
          [name]:
            digitsOnly,
        })
      );

      return;
    }

    // --------------------------------------------------------
    // ✅ REFERRAL PHONE FIX
    // --------------------------------------------------------

    if (
      name === 'referPhone'
    ) {
      const digitsOnly =
        value
          .replace(/\D/g, '')
          .slice(0, 10);

      setFormData(
        (prevData) => ({
          ...prevData,
          referPhone:
            digitsOnly,
        })
      );

      return;
    }

    // --------------------------------------------------------
    // AADHAAR
    // --------------------------------------------------------

    if (
      name ===
      'aadharCardNo'
    ) {
      const digitsOnly =
        value
          .replace(/\D/g, '')
          .slice(0, 12);

      setFormData(
        (prevData) => ({
          ...prevData,
          aadharCardNo:
            digitsOnly,
        })
      );

      return;
    }

    // --------------------------------------------------------
    // NORMAL FIELDS
    // --------------------------------------------------------

    setFormData(
      (prevData) => ({
        ...prevData,

        [name]:
          type ===
          'checkbox'
            ? checked
            : value,
      })
    );
  };

  // ==========================================================
  // BATCH CHANGE
  // ==========================================================

  const handleBatchChange =
    (event) => {
      const {
        value,
      } = event.target;

      setFormData(
        (prev) => ({
          ...prev,

          batch:
            typeof value ===
            'string'
              ? value.split(',')
              : value,
        })
      );
    };

  // ==========================================================
  // Download a print-ready blank enquiry form using the same branding and PDF settings as Admission.
  const handleDownloadBlankEnquiryForm = async () => {
    try {
      const branchId = selectedBranchId;
      let branchDetails = {};

      if (branchId) {
        try {
          const response = await axios.get(
            `${API_BASE_URL}/api/master/branch/${branchId}/`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken') || ''}`,
              },
            }
          );
          branchDetails = response.data || {};
        } catch (error) {
          // Continue with organization fallback if branch details are unavailable.
          console.warn('Unable to fetch branch details for blank enquiry form:', error);
        }
      }

      const organization = branchDetails?.organization_details || branchDetails?.organization_data || branchDetails?.organization;
      const organizationName =
        branchDetails?.organization_name ||
        branchDetails?.org_name ||
        (organization && typeof organization === 'object'
          ? organization.name || organization.organization_name
          : '') ||
        'INSTITUTION NAME';
      const branchName = branchDetails?.branch_name || branchDetails?.name || branchDetails?.branch || '';

      const rawLogo =
        branchDetails?.logo_url || branchDetails?.branch_logo_url || branchDetails?.logo ||
        branchDetails?.image_url || branchDetails?.image || branchDetails?.branch_logo || '';
      const logoUrl = typeof rawLogo === 'string'
        ? (/^https?:\/\//i.test(rawLogo)
            ? rawLogo
            : rawLogo
              ? `${String(API_BASE_URL || '').replace(/\/$/, '')}/${rawLogo.replace(/^\//, '')}`
              : '')
        : rawLogo?.url || rawLogo?.image_url || '';

      const loadImage = (url) => new Promise((resolve, reject) => {
        if (!url) return resolve(null);
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = image.naturalWidth || image.width;
            canvas.height = image.naturalHeight || image.height;
            canvas.getContext('2d').drawImage(image, 0, 0);
            resolve(canvas.toDataURL('image/png'));
          } catch (error) { reject(error); }
        };
        image.onerror = reject;
        image.src = url;
      });

      let logoBase64 = null;
      try { logoBase64 = await loadImage(logoUrl); }
      catch (error) { console.warn('Selected branch logo could not be embedded in the PDF:', error); }

      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pageWidth - margin * 2;
      const toRgb = (hex, fallback) => {
        if (typeof hex !== 'string') return fallback;
        const clean = hex.trim().replace(/^#/, '');
        if (!/^[0-9a-f]{6}$/i.test(clean)) return fallback;
        return [parseInt(clean.slice(0, 2), 16), parseInt(clean.slice(2, 4), 16), parseInt(clean.slice(4, 6), 16)];
      };
      const accent = toRgb(pdfThemeColor, [31, 78, 121]);
      const ink = toRgb(pdfTextColor, [38, 48, 58]);
      const muted = [95, 107, 119];
      const border = [185, 194, 204];
      const pale = accent.map((value) => Math.round(value + (255 - value) * 0.9));
      const drawSection = (title, y) => {
        doc.setFillColor(...pale);
        doc.roundedRect(margin, y, contentWidth, 6.5, 1, 1, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        doc.setTextColor(...accent);
        doc.text(title.toUpperCase(), margin + 2.5, y + 4.4);
        return y + 9;
      };
      const drawField = (label, x, y, width, height = 9) => {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.4);
        doc.setTextColor(...ink);
        doc.text(label, x, y);
        doc.setDrawColor(...border);
        doc.setLineWidth(0.25);
        doc.line(x, y + height - 1.5, x + width, y + height - 1.5);
      };
      const drawCheckbox = (label, x, y) => {
        doc.setDrawColor(...muted);
        doc.setLineWidth(0.25);
        doc.rect(x, y - 2.6, 2.8, 2.8);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.2);
        doc.setTextColor(...ink);
        doc.text(label, x + 4, y - 0.2);
      };

      doc.setDrawColor(...accent);
      doc.setLineWidth(0.7);
      doc.line(margin, 8, pageWidth - margin, 8);
      if (logoBase64) {
        try { doc.addImage(logoBase64, 'PNG', margin, 11, 17, 17, undefined, 'FAST'); }
        catch (error) { console.warn('Unable to place branch logo in PDF:', error); }
      }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(...ink);
      doc.text(doc.splitTextToSize(String(organizationName), 125), pageWidth / 2, 16, { align: 'center', maxWidth: 125 });
      if (branchName) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...muted);
        doc.text(doc.splitTextToSize(String(branchName), 120), pageWidth / 2, 22, { align: 'center', maxWidth: 120 });
      }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(...accent);
      doc.text('ENQUIRY FORM', pageWidth / 2, 31, { align: 'center' });
      doc.setDrawColor(...border);
      doc.setLineWidth(0.3);
      doc.line(margin, 37, pageWidth - margin, 37);

      let y = 41;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...muted);
      doc.text('Please complete the details below in clear BLOCK LETTERS.', margin, y);
      y += 5;

      y = drawSection('Enquiry Details', y);
      const colGap = 6;
      const colW = (contentWidth - colGap) / 2;
      drawField('Enquiry Date', margin, y, colW);
      drawField('Enquiry Code (Office Use)', margin + colW + colGap, y, colW);
      y += 10;
      drawField('Candidate / Client Name *', margin, y, colW);
      drawField('Contact Person', margin + colW + colGap, y, colW);
      y += 10;
      drawField('Mobile Number *', margin, y, colW);
      drawField('Alternate Mobile Number', margin + colW + colGap, y, colW);
      y += 10;
      drawField('Email Address', margin, y, colW);
      drawField('Aadhaar Number', margin + colW + colGap, y, colW);
      y += 10;
      drawField('Designation', margin, y, colW);
      drawField('Nationality', margin + colW + colGap, y, colW);
      y += 10;
      drawField('Full Address', margin, y, contentWidth, 11);
      y += 13;

      y = drawSection('Course & Follow-up Details', y);
      drawField('Interested Course / Programme', margin, y, colW);
      drawField('Enquiry Source', margin + colW + colGap, y, colW);
      y += 10;
      drawField('Assigned To', margin, y, colW);
      drawField('Next Follow-up Date', margin + colW + colGap, y, colW);
      y += 10;
      drawField('Preferred Time', margin, y, colW);
      drawField('Follow-up Medium', margin + colW + colGap, y, colW);
      y += 10;
      drawField('Status', margin, y, colW);
      drawField('Person to Enroll (if different)', margin + colW + colGap, y, colW);
      y += 12;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.4);
      doc.setTextColor(...ink);
      doc.text('Enquiry Type:', margin, y);
      drawCheckbox('Individual', margin + 25, y + 0.5);
      drawCheckbox('Corporate', margin + 57, y + 0.5);
      drawCheckbox('Send Email', margin + 91, y + 0.5);
      drawCheckbox('Send SMS', margin + 125, y + 0.5);
      y += 8;

      y = drawSection('Remarks & Office Use', y);
      drawField('Remarks / Notes', margin, y, contentWidth, 17);
      y += 20;
      drawField('Enquiry Taken By', margin, y, colW, 9);
      drawField('Date', margin + colW + colGap, y, colW, 9);
      y += 11;
      drawField('Applicant Signature', margin, y, colW, 9);
      drawField('Verified By (Office Use)', margin + colW + colGap, y, colW, 9);

      const footerY = pageHeight - 8;
      doc.setDrawColor(...border);
      doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(6.7);
      doc.setTextColor(...muted);
      doc.text('Thank you for your interest. Our team will contact you for the next steps.', pageWidth / 2, footerY, { align: 'center', maxWidth: contentWidth });

      if (y > footerY - 8) {
        console.warn('Enquiry form fields are close to the footer; review the one-page print layout.');
      }
      doc.save('blank-enquiry-form.pdf');
      setSnackbarMessage('Blank enquiry form downloaded successfully');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    } catch (error) {
      console.error('Failed to generate blank enquiry form:', error);
      setSnackbarMessage('Could not generate the blank enquiry form. Please try again.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };


  // SAVE ENQUIRY
  // ==========================================================

  const handleSave = async (
    e
  ) => {
    e.preventDefault();

    // --------------------------------------------------------
    // AADHAAR VALIDATION
    // --------------------------------------------------------

    if (
      formData.aadharCardNo &&
      formData.aadharCardNo.length !==
        12
    ) {
      setSnackbarMessage(
        'Aadhaar number must be exactly 12 digits!'
      );

      setOpenSnackbar(true);

      setSnackbarSeverity(
        'error'
      );

      return;
    }

    // --------------------------------------------------------
    // REFERRAL VALIDATION
    // --------------------------------------------------------

    if (
      formData.anyPersonToEnroll
    ) {
      if (
        !formData.referName ||
        !formData.referEmail ||
        !formData.referPhone
      ) {
        setSnackbarMessage(
          'Please fill all referral details (Name, Email, Phone)!'
        );

        setOpenSnackbar(true);

        setSnackbarSeverity(
          'error'
        );

        return;
      }

      // ✅ Referral phone must be exactly 10 digits
      if (
        formData.referPhone.length !==
        10
      ) {
        setSnackbarMessage(
          'Referral phone number must be exactly 10 digits!'
        );

        setOpenSnackbar(true);

        setSnackbarSeverity(
          'error'
        );

        return;
      }
    }

    // --------------------------------------------------------
    // DEMO VALIDATION
    // --------------------------------------------------------

    if (
      formData.scheduleDemoFlag
    ) {
      if (
        !formData.demoCourse.length ||
        !formData.demoBatch.length ||
        !formData.demoFaculty.length ||
        !formData.demoDate ||
        !formData.demoTime
      ) {
        setSnackbarMessage(
          'Please complete all demo details (Course, Batch, Faculty, Date, Time)!'
        );

        setOpenSnackbar(true);

        setSnackbarSeverity(
          'error'
        );

        return;
      }
    }

    // --------------------------------------------------------
    // REQUIRED VALIDATION
    // --------------------------------------------------------

    if (
      !formData.clientName ||
      !formData.mobileNo ||
      !formData.email ||
      !formData.address ||
      !formData.followUpMedium ||
      !formData.enquirySource ||
      !formData.assignedTo ||
      !formData.status ||
      !formData.nationality ||
      !formData.course.length
    ) {
      setSnackbarMessage(
        'Please fill all required fields!'
      );

      setOpenSnackbar(true);

      setSnackbarSeverity(
        'error'
      );

      return;
    }

    // --------------------------------------------------------
    // SUBMIT
    // --------------------------------------------------------

    setIsSubmitting(true);

    try {
      const payload = {
        enquiry_date:
          formData.enquiryDate,

        candidate_name:
          formData.clientName,

        aadhar_card_no:
          formData.aadharCardNo ||
          null,

        mobile_no:
          `${mobileCountryCode}${formData.mobileNo}`,

        alternate_mobile_no:
          formData.alternateMobileNo
            ? `${altMobileCountryCode}${formData.alternateMobileNo}`
            : null,

        email:
          formData.email,

        address:
          formData.address,

        next_followup_date:
          formData.nextFollowUp,

        // ✅ 10 AM default unless changed
        preferred_time:
          formData.preferredTime ||
          '10:00',

        remark:
          formData.remark ||
          null,

        refer_name:
          formData.anyPersonToEnroll
            ? formData.referName ||
              null
            : null,

        refer_email:
          formData.anyPersonToEnroll
            ? formData.referEmail ||
              null
            : null,

        refer_phone:
          formData.anyPersonToEnroll
            ? formData.referPhone ||
              null
            : null,

        send_mail:
          formData.sendMail,

        send_sms:
          formData.sendTextSMS,

        schedule_demo:
          formData.scheduleDemoFlag,

        demo_date:
          formData.scheduleDemoFlag
            ? formData.demoDate ||
              null
            : null,

        demo_time:
          formData.scheduleDemoFlag
            ? formData.demoTime ||
              null
            : null,

        followup_medium:
          parseInt(
            formData.followUpMedium
          ),

        enquiry_source:
          parseInt(
            formData.enquirySource
          ),

        assigned_to:
          parseInt(
            formData.assignedTo
          ),

        status:
          parseInt(
            formData.status
          ),

        nationality:
          parseInt(
            formData.nationality
          ),

        demo_course:
          formData.scheduleDemoFlag &&
          formData.demoCourse.length >
            0
            ? parseInt(
                formData.demoCourse[0]
              )
            : null,

        demo_batch:
          formData.scheduleDemoFlag &&
          formData.demoBatch.length >
            0
            ? parseInt(
                formData.demoBatch[0]
              )
            : null,

        demo_faculty:
          formData.scheduleDemoFlag &&
          formData.demoFaculty.length >
            0
            ? parseInt(
                formData.demoFaculty[0]
              )
            : null,

        courses:
          formData.course.map(
            (id) =>
              parseInt(id)
          ),
      };

      // ------------------------------------------------------
      // REMOVE NULL / UNDEFINED
      // ------------------------------------------------------

      Object.keys(payload).forEach(
        (key) => {
          if (
            payload[key] ===
              null ||
            payload[key] ===
              undefined
          ) {
            delete payload[key];
          }
        }
      );

      console.log(
        '📤 Sending enquiry payload:',
        payload
      );

      const accessToken =
        localStorage.getItem(
          'accessToken'
        );

      // ------------------------------------------------------
      // CREATE ENQUIRY
      // ------------------------------------------------------

          const response =
        await fetch(
          `${API_BASE_URL}/api/student_details/enquiries/?organization=${organizationId}&branch=${selectedBranchId}`,
          {
            method: 'POST',

            headers: {
              'Content-Type':
                'application/json',

              Authorization:
                `Bearer ${accessToken}`,
            },

            body:
              JSON.stringify(
                payload
              ),
          }
        );

      // ------------------------------------------------------
      // ERROR
      // ------------------------------------------------------

      if (!response.ok) {
        let errorData;

        let errorMessage =
          'Failed to save enquiry';

        try {
          const responseText =
            await response.text();

          console.error(
            '❌ API Error Response:',
            responseText
          );

          try {
            errorData =
              JSON.parse(
                responseText
              );

            if (
              errorData.error
            ) {
              errorMessage =
                errorData.error;
            } else if (
              errorData.message
            ) {
              errorMessage =
                errorData.message;
            } else if (
              errorData.detail
            ) {
              errorMessage =
                errorData.detail;
            } else if (
              typeof errorData ===
              'object'
            ) {
              const fieldErrors =
                Object.entries(
                  errorData
                )
                  .map(
                    ([
                      field,
                      errors,
                    ]) => {
                      if (
                        Array.isArray(
                          errors
                        )
                      ) {
                        return `${field}: ${errors.join(
                          ', '
                        )}`;
                      }

                      return `${field}: ${errors}`;
                    }
                  )
                  .join(
                    '; '
                  );

              errorMessage =
                fieldErrors ||
                JSON.stringify(
                  errorData
                );
            }
          } catch (
            parseError
          ) {
            errorMessage =
              responseText ||
              `Server error: ${response.status}`;
          }
        } catch (
          readError
        ) {
          errorMessage =
            `Server error: ${response.status} - Unable to read error response`;
        }

        throw new Error(
          errorMessage
        );
      }

      // ------------------------------------------------------
      // SUCCESS
      // ------------------------------------------------------

      const result =
        await response.json();

      console.log(
        '✅ Success response:',
        result
      );

      setShowSuccessModal(
        true
      );

      setSnackbarMessage(
        'Enquiry added successfully!'
      );

      setSnackbarSeverity(
        'success'
      );

      setOpenSnackbar(true);

    } catch (error) {
      console.error(
        '💥 Error saving enquiry:',
        error
      );

      setSnackbarMessage(
        error.message ||
          'Failed to save enquiry. Please try again.'
      );

      setSnackbarSeverity(
        'error'
      );

      setOpenSnackbar(true);

    } finally {
      setIsSubmitting(false);
    }
  };

  // ==========================================================
  // RESET FORM
  // ==========================================================

  const handleCancel = () => {
    setFormData(
      getInitialFormData()
    );

    setMobileCountryCode(
      '+91'
    );

    setAltMobileCountryCode(
      '+91'
    );

    setTempCourse([]);

    setTempBatch([]);

    setTempDemoCourse([]);

    setTempDemoBatch([]);

    setFilteredBatches([]);

    setFilteredDemoBatches([]);

    setFilteredDemoFaculty([]);
  };

  // ==========================================================
  // COUNTRY SELECT
  // ==========================================================

  const CountryCodeSelect = ({
    value,
    onChange,
  }) => (
    <InputAdornment
      position="start"
      sx={{ mr: 0 }}
    >
      <Select
        value={value}
        onChange={(e) =>
          onChange(
            e.target.value
          )
        }
        variant="standard"
        disableUnderline
        sx={{
          minWidth: 80,
          fontSize:
            '0.875rem',

          '& .MuiSelect-select':
            {
              py: 0,
              pr:
                '24px !important',
              pl: 0.5,
              display: 'flex',
              alignItems:
                'center',
            },

          '& .MuiSelect-icon':
            {
              right: 0,
            },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 300,
            },
          },
        }}
      >
        {COUNTRY_CODES.map(
          (country, idx) => (
            <MenuItem
              key={`${country.code}-${country.country}-${idx}`}
              value={
                country.code
              }
            >
              <Box
                sx={{
                  display:
                    'flex',
                  alignItems:
                    'center',
                  gap: 0.5,
                }}
              >
                <span>
                  {
                    country.flag
                  }
                </span>

                <Typography
                  variant="body2"
                >
                  {
                    country.code
                  }
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    ml: 0.25,
                  }}
                >
                  {
                    country.country
                  }
                </Typography>
              </Box>
            </MenuItem>
          )
        )}
      </Select>

      <Box
        sx={{
          width: '1px',
          height: '24px',
          backgroundColor:
            theme.palette.divider,
          mx: 1,
        }}
      />
    </InputAdornment>
  );

  // ==========================================================
  // COMMON FIELD STYLE
  // ==========================================================

  const fieldStyle = {
    width: '100%',

    '& .MuiInputBase-root':
      {
        width: '100%',
      },

    '& .MuiInputLabel-root':
      {
        color:
          theme.palette.text
            .primary,
      },

    '& .MuiInputLabel-root.Mui-focused':
      {
        color:
          theme.palette.primary
            .main,
      },

    '& .MuiOutlinedInput-notchedOutline':
      {
        borderColor:
          theme.palette.text
            .primary,
      },

    '&:hover .MuiOutlinedInput-notchedOutline':
      {
        borderColor:
          theme.palette.primary
            .main,
      },

    '& input::-webkit-calendar-picker-indicator':
      {
        filter:
          theme.palette
            .mode === 'dark'
            ? 'invert(1)'
            : 'none',
      },
  };

  // ==========================================================
  // LOADING
  // ==========================================================

  if (isLoading) {
    return (
      <Container
        maxWidth="lg"
        sx={{
          my: 4,
          display: 'flex',
          justifyContent:
            'center',
          alignItems: 'center',
          minHeight:
            '60vh',
        }}
      >
        <CircularProgress
          size={60}
        />
      </Container>
    );
  }

  // ==========================================================
  // UI
  // ==========================================================

  return (
    <Container
      maxWidth="lg"
      sx={{ my: 0 }}
    >
      <Box
        sx={{
          p: 4,
          borderRadius: 2,
          backgroundColor:
            theme.palette
              .background
              .paper,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
            mb: 3,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 'bold',
              color: theme.palette.primary.main,
              mb: 0,
            }}
          >
            New Enquiry
          </Typography>
          <Button
            variant="contained"
            onClick={handleDownloadBlankEnquiryForm}
            startIcon={<FileDownloadIcon />}
            sx={{
              minHeight: 42,
              borderRadius: 2,
              px: 2.5,
              fontWeight: 700,
              textTransform: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            Download Blank Form (PDF)
          </Button>
        </Box>

        <form
          onSubmit={
            handleSave
          }
        >
          {/* ==================================================
              CHECKBOXES
          ================================================== */}

          <FormGroup
            row
            sx={{ mb: 3 }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  name="sendMail"
                  checked={
                    formData.sendMail
                  }
                  onChange={
                    handleChange
                  }
                  sx={{
                    color:
                      theme.palette
                        .mode ===
                      'dark'
                        ? '#fff'
                        : theme.palette
                            .text
                            .primary,

                    '&.Mui-checked':
                      {
                        color:
                          theme.palette
                            .primary
                            .main,
                      },
                  }}
                />
              }
              label="Send Mail"
              sx={{
                '& .MuiTypography-root':
                  {
                    color:
                      theme.palette
                        .text
                        .primary,
                  },
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="sendTextSMS"
                  checked={
                    formData.sendTextSMS
                  }
                  onChange={
                    handleChange
                  }
                  sx={{
                    color:
                      theme.palette
                        .mode ===
                      'dark'
                        ? '#fff'
                        : theme.palette
                            .text
                            .primary,

                    '&.Mui-checked':
                      {
                        color:
                          theme.palette
                            .primary
                            .main,
                      },
                  }}
                />
              }
              label="Send Text SMS"
              sx={{
                '& .MuiTypography-root':
                  {
                    color:
                      theme.palette
                        .text
                        .primary,
                  },
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="corporate"
                  checked={
                    formData.corporate
                  }
                  onChange={
                    handleChange
                  }
                  sx={{
                    color:
                      theme.palette
                        .mode ===
                      'dark'
                        ? '#fff'
                        : theme.palette
                            .text
                            .primary,

                    '&.Mui-checked':
                      {
                        color:
                          theme.palette
                            .primary
                            .main,
                      },
                  }}
                />
              }
              label="Corporate"
              sx={{
                '& .MuiTypography-root':
                  {
                    color:
                      theme.palette
                        .text
                        .primary,
                  },
              }}
            />
          </FormGroup>

          {/* ==================================================
              FORM FIELDS
          ================================================== */}

          <Box
            sx={{
              display:
                'flex',
              flexDirection:
                'column',
              gap: 3,
            }}
          >

            {/* Enquiry Date */}

            <TextField
              label="Enquiry Date"
              type="date"
              variant="outlined"
              fullWidth
              name="enquiryDate"
              value={
                formData.enquiryDate
              }
              onChange={
                handleChange
              }
              InputLabelProps={{
                shrink: true,
              }}
              sx={fieldStyle}
            />

            {/* Candidate Name */}

            <TextField
              label="Candidate Name"
              variant="outlined"
              fullWidth
              name="clientName"
              value={
                formData.clientName
              }
              onChange={
                handleChange
              }
              required
              sx={fieldStyle}
            />

            {/* Aadhar */}

            <TextField
              label="Aadhar Card No."
              variant="outlined"
              fullWidth
              name="aadharCardNo"
              value={
                formData.aadharCardNo
              }
              onChange={
                handleChange
              }
              inputProps={{
                maxLength: 12,
                inputMode:
                  'numeric',
              }}
              helperText={
                formData.aadharCardNo
                  .length >
                  0 &&
                formData.aadharCardNo
                  .length !==
                  12
                  ? `Aadhaar number must be exactly 12 digits (currently ${formData.aadharCardNo.length}/12)`
                  : ''
              }
              error={
                formData.aadharCardNo
                  .length >
                  0 &&
                formData.aadharCardNo
                  .length !==
                  12
              }
              sx={fieldStyle}
            />

            {/* Mobile */}

            <TextField
              label="Mobile No."
              variant="outlined"
              fullWidth
              name="mobileNo"
              value={
                formData.mobileNo
              }
              onChange={
                handleChange
              }
              required
              inputProps={{
                maxLength: 10,
                inputMode:
                  'numeric',
              }}
              helperText={
                formData.mobileNo
                  .length >
                  0 &&
                formData.mobileNo
                  .length <
                  10
                  ? `${
                      10 -
                      formData.mobileNo
                        .length
                    } more digit(s) required`
                  : ''
              }
              InputProps={{
                startAdornment:
                  (
                    <CountryCodeSelect
                      value={
                        mobileCountryCode
                      }
                      onChange={
                        setMobileCountryCode
                      }
                    />
                  ),
              }}
              sx={fieldStyle}
            />

            {/* Alternate Mobile */}

            <TextField
              label="Alternate Mobile No."
              variant="outlined"
              fullWidth
              name="alternateMobileNo"
              value={
                formData.alternateMobileNo
              }
              onChange={
                handleChange
              }
              inputProps={{
                maxLength: 10,
                inputMode:
                  'numeric',
              }}
              helperText={
                formData.alternateMobileNo
                  .length >
                  0 &&
                formData.alternateMobileNo
                  .length <
                  10
                  ? `${
                      10 -
                      formData
                        .alternateMobileNo
                        .length
                    } more digit(s) required`
                  : ''
              }
              InputProps={{
                startAdornment:
                  (
                    <CountryCodeSelect
                      value={
                        altMobileCountryCode
                      }
                      onChange={
                        setAltMobileCountryCode
                      }
                    />
                  ),
              }}
              sx={fieldStyle}
            />

            {/* Email */}

            <TextField
              label="Email"
              type="email"
              variant="outlined"
              fullWidth
              name="email"
              value={
                formData.email
              }
              onChange={
                handleChange
              }
              required
              sx={fieldStyle}
            />

            {/* Address */}

            <TextField
              label="Address"
              variant="outlined"
              fullWidth
              name="address"
              value={
                formData.address
              }
              onChange={
                handleChange
              }
              required
              sx={fieldStyle}
            />

            {/* ==================================================
                ANY PERSON TO ENROLL
            ================================================== */}

            <FormControl
              component="fieldset"
              sx={{ mb: 2 }}
            >
              <Typography
                component="legend"
                sx={{
                  color:
                    theme.palette
                      .text
                      .primary,
                  mb: 1,
                }}
              >
                Any Person You Would Like To Enroll? *
              </Typography>

              <RadioGroup
                row
                name="anyPersonToEnroll"
                value={
                  formData.anyPersonToEnroll
                    ? 'yes'
                    : 'no'
                }
                onChange={
                  handleAnyPersonChange
                }
              >
                <FormControlLabel
                  value="yes"
                  control={
                    <Radio
                      sx={{
                        color:
                          theme
                            .palette
                            .text
                            .primary,
                      }}
                    />
                  }
                  label="Yes"
                  sx={{
                    color:
                      theme.palette
                        .text
                        .primary,
                  }}
                />

                <FormControlLabel
                  value="no"
                  control={
                    <Radio
                      sx={{
                        color:
                          theme
                            .palette
                            .text
                            .primary,
                      }}
                    />
                  }
                  label="No"
                  sx={{
                    color:
                      theme.palette
                        .text
                        .primary,
                  }}
                />
              </RadioGroup>
            </FormControl>

            {/* Referral Fields */}

            {formData.anyPersonToEnroll && (
              <Box
                sx={{
                  display:
                    'flex',
                  flexDirection:
                    'column',
                  gap: 2,
                  mb: 2,
                  pl: 2,
                  borderLeft:
                    `2px solid ${theme.palette.primary.main}`,
                }}
              >
                <TextField
                  label="Refer Name *"
                  variant="outlined"
                  fullWidth
                  name="referName"
                  value={
                    formData.referName
                  }
                  onChange={
                    handleChange
                  }
                  required
                  sx={fieldStyle}
                />

                <TextField
                  label="Refer Email *"
                  type="email"
                  variant="outlined"
                  fullWidth
                  name="referEmail"
                  value={
                    formData.referEmail
                  }
                  onChange={
                    handleChange
                  }
                  required
                  sx={fieldStyle}
                />

                {/* ✅ REFERRAL PHONE FIX */}

                <TextField
                  label="Refer Phone *"
                  variant="outlined"
                  fullWidth
                  name="referPhone"
                  value={
                    formData.referPhone
                  }
                  onChange={
                    handleChange
                  }
                  required
                  inputProps={{
                    maxLength: 10,
                    inputMode:
                      'numeric',
                  }}
                  error={
                    formData.referPhone
                      .length >
                      0 &&
                    formData.referPhone
                      .length !==
                      10
                  }
                  helperText={
                    formData.referPhone
                      .length >
                      0 &&
                    formData.referPhone
                      .length <
                      10
                      ? `${
                          10 -
                          formData
                            .referPhone
                            .length
                        } more digit(s) required`
                      : ''
                  }
                  sx={fieldStyle}
                />
              </Box>
            )}

            {/* Next Follow Up */}

            <TextField
              label="Next Follow Up"
              type="date"
              variant="outlined"
              fullWidth
              name="nextFollowUp"
              value={
                formData.nextFollowUp
              }
              onChange={
                handleChange
              }
              InputLabelProps={{
                shrink: true,
              }}
              sx={fieldStyle}
            />

            {/* ✅ Preferred Time = 10 AM */}

            <TextField
              label="Preferred Time"
              type="time"
              variant="outlined"
              fullWidth
              name="preferredTime"
              value={
                formData.preferredTime
              }
              onChange={
                handleChange
              }
              InputLabelProps={{
                shrink: true,
              }}
              sx={fieldStyle}
            />

            {/* Follow Up Medium */}

            <FormControl
              fullWidth
              required
              sx={fieldStyle}
            >
              <InputLabel
                id="followUpMedium-label"
                sx={{
                  color:
                    theme.palette
                      .text
                      .primary,

                  '&.Mui-focused':
                    {
                      color:
                        theme.palette
                          .primary
                          .main,
                    },
                }}
              >
                Follow Up Medium
              </InputLabel>

              <Select
                labelId="followUpMedium-label"
                id="followUpMedium"
                name="followUpMedium"
                value={
                  formData.followUpMedium
                }
                label="Follow Up Medium"
                onChange={
                  handleChange
                }
              >
                <MenuItem value="">
                  <em>
                    Select a medium
                  </em>
                </MenuItem>

                {followUpMediumData.map(
                  (medium) => (
                    <MenuItem
                      key={
                        medium.id
                      }
                      value={
                        medium.id
                      }
                    >
                      {
                        medium.name
                      }
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>

            {/* Enquiry Source */}

            <FormControl
              fullWidth
              required
              sx={fieldStyle}
            >
              <InputLabel id="enquirySource-label">
                Enquiry Source
              </InputLabel>

              <Select
                labelId="enquirySource-label"
                id="enquirySource"
                name="enquirySource"
                value={
                  formData.enquirySource
                }
                label="Enquiry Source"
                onChange={
                  handleChange
                }
              >
                <MenuItem value="">
                  <em>
                    Select a source
                  </em>
                </MenuItem>

                {enquirySourceData.map(
                  (source) => (
                    <MenuItem
                      key={
                        source.id
                      }
                      value={
                        source.id
                      }
                    >
                      {
                        source.name
                      }
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>

            {/* ✅ ASSIGNED TO FIX */}

            <FormControl
              fullWidth
              required
              sx={fieldStyle}
            >
              <InputLabel id="assignedTo-label">
                Assigned To
              </InputLabel>

              <Select
                labelId="assignedTo-label"
                id="assignedTo"
                name="assignedTo"
                value={
                  formData.assignedTo
                }
                label="Assigned To"
                onChange={
                  handleChange
                }
              >
                <MenuItem value="">
                  <em>
                    Select an assignee
                  </em>
                </MenuItem>

                {assignedToData.length >
                0 ? (
                  assignedToData.map(
                    (assignee) => (
                      <MenuItem
                        key={
                          assignee.id ||
                          assignee.employeeId
                        }
                        value={
                          assignee.id ||
                          assignee.employeeId
                        }
                      >
                        {assignee.name ||
                          assignee.employee_name ||
                          `${assignee.first_name || ''} ${
                            assignee.last_name || ''
                          }`.trim() ||
                          `Counselor ${
                            assignee.id ||
                            assignee.employeeId
                          }`}
                      </MenuItem>
                    )
                  )
                ) : (
                  <MenuItem
                    value=""
                    disabled
                  >
                    No counselors
                    available
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            {/* Status */}

            <FormControl
              fullWidth
              required
              sx={fieldStyle}
            >
              <InputLabel id="status-label">
                Status
              </InputLabel>

              <Select
                labelId="status-label"
                id="status"
                name="status"
                value={
                  formData.status
                }
                label="Status"
                onChange={
                  handleChange
                }
              >
                <MenuItem value="">
                  <em>
                    Select a status
                  </em>
                </MenuItem>

                {statusData.map(
                  (status) => (
                    <MenuItem
                      key={
                        status.id
                      }
                      value={
                        status.id
                      }
                    >
                      {
                        status.name
                      }
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>

            {/* Nationality */}

            <FormControl
              fullWidth
              required
              sx={fieldStyle}
            >
              <InputLabel id="nationality-label">
                Nationality
              </InputLabel>

              <Select
                labelId="nationality-label"
                id="nationality"
                name="nationality"
                value={
                  formData.nationality
                }
                label="Nationality"
                onChange={
                  handleChange
                }
              >
                <MenuItem value="">
                  <em>
                    Select a nationality
                  </em>
                </MenuItem>

                {nationalityData.map(
                  (nationality) => (
                    <MenuItem
                      key={
                        nationality.id
                      }
                      value={
                        nationality.id
                      }
                    >
                      {
                        nationality.name
                      }
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>

            {/* Courses */}

            <FormControl
              fullWidth
              required
              sx={fieldStyle}
            >
              <InputLabel
                shrink={
                  formData.course
                    .length >
                  0
                }
                id="course-label"
              >
                Courses
              </InputLabel>

              <Select
                labelId="course-label"
                id="course"
                name="course"
                multiple
                label="Courses"
                value={
                  tempCourse
                }
                open={
                  courseOpen
                }
                onOpen={() => {
                  setTempCourse([
                    ...formData.course,
                  ]);

                  setCourseOpen(
                    true
                  );
                }}
                renderValue={(
                  selected
                ) => (
                  <Box
                    sx={{
                      display:
                        'flex',
                      flexWrap:
                        'wrap',
                      gap: 0.5,
                    }}
                  >
                    {selected.map(
                      (value) => {
                        const course =
                          courseData.find(
                            (c) =>
                              c.id ===
                              value
                          );

                        return (
                          <Chip
                            key={
                              value
                            }
                            label={
                              course?.name ||
                              course?.course_name ||
                              `Course ${value}`
                            }
                            size="small"
                            onDelete={() => {
                              setTempCourse(
                                tempCourse.filter(
                                  (id) =>
                                    id !==
                                    value
                                )
                              );
                            }}
                            deleteIcon={
                              <CloseIcon />
                            }
                            sx={{
                              backgroundColor:
                                theme
                                  .palette
                                  .primary
                                  .main,
                              color:
                                theme
                                  .palette
                                  .primary
                                  .contrastText,
                            }}
                          />
                        );
                      }
                    )}
                  </Box>
                )}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 400,
                    },
                  },
                }}
              >
                {courseData.map(
                  (course) => (
                    <MenuItem
                      key={
                        course.id
                      }
                      value={
                        course.id
                      }
                    >
                      <Checkbox
                        checked={
                          tempCourse.indexOf(
                            course.id
                          ) > -1
                        }
                        onChange={(
                          e
                        ) => {
                          e.stopPropagation();

                          const currentIndex =
                            tempCourse.indexOf(
                              course.id
                            );

                          const newSelected =
                            [
                              ...tempCourse,
                            ];

                          if (
                            currentIndex ===
                            -1
                          ) {
                            newSelected.push(
                              course.id
                            );
                          } else {
                            newSelected.splice(
                              currentIndex,
                              1
                            );
                          }

                          setTempCourse(
                            newSelected
                          );
                        }}
                      />

                      {course.name ||
                        course.course_name ||
                        `Course ${course.id}`}
                    </MenuItem>
                  )
                )}

                <ListSubheader
                  sx={{
                    position:
                      'sticky',
                    bottom: 0,
                    bgcolor:
                      theme
                        .palette
                        .background
                        .paper,
                    borderTop:
                      `1px solid ${theme.palette.divider}`,
                    p: 1,
                    display:
                      'flex',
                    justifyContent:
                      'flex-end',
                  }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                      setFormData(
                        (prev) => ({
                          ...prev,
                          course:
                            tempCourse,
                          demoCourse:
                            tempCourse.length >
                            0
                              ? [
                                  tempCourse[0],
                                ]
                              : [],
                        })
                      );

                      setCourseOpen(
                        false
                      );
                    }}
                  >
                    OK
                  </Button>
                </ListSubheader>
              </Select>
            </FormControl>

            {/* Schedule Demo */}

            <FormControlLabel
              control={
                <Checkbox
                  name="scheduleDemoFlag"
                  checked={
                    formData.scheduleDemoFlag
                  }
                  onChange={
                    handleDemoCheckboxChange
                  }
                  sx={{
                    color:
                      theme.palette
                        .mode ===
                      'dark'
                        ? '#fff'
                        : theme.palette
                            .text
                            .primary,

                    '&.Mui-checked':
                      {
                        color:
                          theme.palette
                            .primary
                            .main,
                      },
                  }}
                />
              }
              label="Schedule a Demo Class"
              sx={{
                '& .MuiTypography-root':
                  {
                    color:
                      theme.palette
                        .text
                        .primary,
                  },

                mt: 2,
                mb: 2,
              }}
            />

            {/* Demo Summary */}

            {formData.scheduleDemoFlag && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  borderRadius: 2,

                  backgroundColor:
                    theme.palette
                      .mode ===
                    'dark'
                      ? 'rgba(25, 118, 210, 0.1)'
                      : 'rgba(25, 118, 210, 0.05)',

                  border:
                    `1px solid ${theme.palette.primary.main}`,
                }}
              >
                <Box
                  sx={{
                    display:
                      'flex',
                    justifyContent:
                      'space-between',
                    alignItems:
                      'center',
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight:
                        'bold',

                      color:
                        theme
                          .palette
                          .primary
                          .main,
                    }}
                  >
                    Demo Class Details
                  </Typography>

                  <Button
                    size="small"
                    onClick={() => {
                      setTempDemoCourse([
                        ...formData.demoCourse,
                      ]);

                      setTempDemoBatch([
                        ...formData.demoBatch,
                      ]);

                      setShowDemoModal(
                        true
                      );
                    }}
                    sx={{
                      textTransform:
                        'none',
                    }}
                  >
                    Edit Demo
                  </Button>
                </Box>

                <Grid
                  container
                  spacing={2}
                >
                  <Grid
                    item
                    xs={12}
                    sm={6}
                  >
                    <Typography
                      variant="caption"
                      color="text.primary"
                    >
                      Course(s)
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight:
                          'medium',
                      }}
                    >
                      {formData
                        .demoCourse
                        .length >
                      0
                        ? formData.demoCourse
                            .map(
                              (
                                courseId
                              ) => {
                                const course =
                                  courseData.find(
                                    (c) =>
                                      c.id ===
                                      courseId
                                  );

                                return (
                                  course?.course_name ||
                                  course?.name ||
                                  `Course ${courseId}`
                                );
                              }
                            )
                            .join(
                              ', '
                            )
                        : 'Not selected'}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={6}
                  >
                    <Typography
                      variant="caption"
                      color="text.primary"
                    >
                      Batch(es)
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight:
                          'medium',
                      }}
                    >
                      {formData
                        .demoBatch
                        .length >
                      0
                        ? formData.demoBatch
                            .map(
                              (
                                batchId
                              ) => {
                                const batch =
                                  batchData.find(
                                    (b) =>
                                      b.id ===
                                      batchId
                                  );

                                return (
                                  batch?.batch_name ||
                                  batch?.name ||
                                  `Batch ${batchId}`
                                );
                              }
                            )
                            .join(
                              ', '
                            )
                        : 'Not selected'}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={4}
                  >
                    <Typography
                      variant="caption"
                      color="text.primary"
                    >
                      Faculty
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight:
                          'medium',
                      }}
                    >
                      {formData
                        .demoFaculty
                        .length >
                      0
                        ? formData.demoFaculty
                            .map(
                              (
                                facultyId
                              ) => {
                                const faculty =
                                  facultyData.find(
                                    (f) =>
                                      f.id ===
                                      parseInt(
                                        facultyId
                                      )
                                  );

                                return (
                                  faculty?.name ||
                                  `${faculty?.first_name || ''} ${
                                    faculty?.last_name || ''
                                  }`.trim() ||
                                  `Faculty ${facultyId}`
                                );
                              }
                            )
                            .join(
                              ', '
                            )
                        : 'Not selected'}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={4}
                  >
                    <Typography
                      variant="caption"
                      color="text.primary"
                    >
                      Demo Date
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight:
                          'medium',
                      }}
                    >
                      {formData.demoDate
                        ? new Date(
                            formData.demoDate
                          ).toLocaleDateString()
                        : 'Not selected'}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={4}
                  >
                    <Typography
                      variant="caption"
                      color="text.primary"
                    >
                      Demo Time
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight:
                          'medium',
                      }}
                    >
                      {formData.demoTime ||
                        'Not selected'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Remark */}

            <TextField
              label="Remark"
              variant="outlined"
              fullWidth
              name="remark"
              value={
                formData.remark
              }
              onChange={
                handleChange
              }
              sx={fieldStyle}
            />
          </Box>

          {/* ==================================================
              BUTTONS
          ================================================== */}

          <Box
            sx={{
              display:
                'flex',
              justifyContent:
                'flex-end',
              gap: 2,
              mt: 4,
            }}
          >
            <Tooltip
              title={
                !formData.clientName
                  ? 'Enter Candidate Name'
                  : !formData.mobileNo
                  ? 'Enter Mobile No.'
                  : !formData.email
                  ? 'Enter Email'
                  : !formData.address
                  ? 'Enter Address'
                  : !formData.followUpMedium
                  ? 'Select Follow Up Medium'
                  : !formData.enquirySource
                  ? 'Select Enquiry Source'
                  : !formData.assignedTo
                  ? 'Select Assigned To'
                  : !formData.status
                  ? 'Select Status'
                  : !formData.nationality
                  ? 'Select Nationality'
                  : !formData.course
                      .length
                  ? 'Select at least one Course'
                  : formData.anyPersonToEnroll &&
                    (
                      !formData.referName ||
                      !formData.referEmail ||
                      !formData.referPhone ||
                      formData.referPhone.length !==
                        10
                    )
                  ? 'Enter valid 10-digit referral phone'
                  : formData.scheduleDemoFlag &&
                    (
                      !formData.demoCourse
                        .length ||
                      !formData.demoBatch
                        .length ||
                      !formData.demoFaculty
                        .length ||
                      !formData.demoDate ||
                      !formData.demoTime
                    )
                  ? 'Complete all demo details'
                  : formData.aadharCardNo &&
                    formData.aadharCardNo.length !==
                      12
                  ? 'Aadhaar number must be 12 digits'
                  : ''
              }
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={
                    isSubmitting ||
                    !formData.clientName ||
                    !formData.mobileNo ||
                    !formData.email ||
                    !formData.address ||
                    !formData.followUpMedium ||
                    !formData.enquirySource ||
                    !formData.assignedTo ||
                    !formData.status ||
                    !formData.nationality ||
                    !formData.course.length ||
                    (
                      formData.anyPersonToEnroll &&
                      (
                        !formData.referName ||
                        !formData.referEmail ||
                        !formData.referPhone ||
                        formData.referPhone.length !==
                          10
                      )
                    ) ||
                    (
                      formData.aadharCardNo &&
                      formData.aadharCardNo.length !==
                        12
                    )
                  }
                  sx={{
                    backgroundColor:
                      theme.palette
                        .primary
                        .main,

                    '&:hover': {
                      backgroundColor:
                        theme.palette
                          .primary
                          .dark,
                    },

                    textTransform:
                      'none',

                    fontWeight:
                      'bold',

                    padding:
                      '8px 16px',

                    minWidth:
                      '100px',
                  }}
                >
                  {isSubmitting ? (
                    <CircularProgress
                      size={24}
                      color="inherit"
                    />
                  ) : (
                    'SAVE'
                  )}
                </Button>
              </span>
            </Tooltip>

            <Button
              variant="outlined"
              color="error"
              onClick={
                handleCancel
              }
              disabled={
                isSubmitting
              }
              sx={{
                textTransform:
                  'none',

                fontWeight:
                  'bold',

                padding:
                  '8px 16px',

                minWidth:
                  '100px',
              }}
            >
              RESET
            </Button>

            <Button
              onClick={() =>
                navigate(-1)
              }
              variant="outlined"
              color="secondary"
              disabled={
                isSubmitting
              }
              sx={{
                textTransform:
                  'none',

                fontWeight:
                  'bold',

                padding:
                  '8px 16px',

                minWidth:
                  '100px',
              }}
            >
              Back
            </Button>
          </Box>
        </form>
      </Box>

      {/* ======================================================
          DEMO MODAL
      ====================================================== */}

      <Modal
        open={
          showDemoModal
        }
        onClose={() =>
          setShowDemoModal(
            false
          )
        }
        BackdropProps={{
          style: {
            backgroundColor:
              'rgba(0, 0, 0, 0.8)',
          },
        }}
      >
        <Box
          sx={{
            position:
              'absolute',

            top: '50%',
            left: '50%',

            transform:
              'translate(-50%, -50%)',

            width: {
              xs: '95%',
              sm: 700,
              md: 800,
            },

            maxHeight:
              '90vh',

            bgcolor:
              theme.palette
                .background
                .paper,

            borderRadius: 2,

            boxShadow: 24,

            overflow:
              'hidden',

            display:
              'flex',

            flexDirection:
              'column',
          }}
        >
          {/* Header */}

          <Box
            sx={{
              backgroundColor:
                theme.palette
                  .primary
                  .main,

              padding:
                '12px 20px',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: '#fff',
                fontWeight:
                  'bold',
              }}
            >
              Schedule Demo Class
            </Typography>
          </Box>

          {/* Content */}

          <Box
            sx={{
              flex: 1,
              overflowY:
                'auto',
              padding: 3,
            }}
          >
            <Box
              sx={{
                display:
                  'flex',

                flexDirection:
                  'column',

                gap: 3,
              }}
            >
              {/* Demo Course */}

              <FormControl
                fullWidth
                required
              >
                <InputLabel
                  shrink={
                    tempDemoCourse.length >
                    0
                  }
                  sx={{
                    color:
                      theme.palette
                        .text
                        .primary,

                    '&.Mui-focused':
                      {
                        color:
                          theme.palette
                            .primary
                            .main,
                      },
                  }}
                >
                  Course *
                </InputLabel>

                <Select
                  multiple
                  value={
                    tempDemoCourse
                  }
                  label="Course *"
                  open={
                    demoCourseOpen
                  }
                  onOpen={() =>
                    setDemoCourseOpen(
                      true
                    )
                  }
                  onClose={() =>
                    setDemoCourseOpen(
                      false
                    )
                  }
                  onChange={(e) => {
                    const {
                      value,
                    } =
                      e.target;

                    setTempDemoCourse(
                      typeof value ===
                        'string'
                        ? value.split(
                            ','
                          )
                        : value
                    );
                  }}
                  renderValue={(
                    selected
                  ) => (
                    <Box
                      sx={{
                        display:
                          'flex',
                        flexWrap:
                          'wrap',
                        gap: 0.5,
                      }}
                    >
                      {selected.map(
                        (value) => {
                          const course =
                            courseData.find(
                              (c) =>
                                c.id ===
                                value
                            );

                          return (
                            <Chip
                              key={
                                value
                              }
                              label={
                                course?.course_name ||
                                course?.name ||
                                `Course ${value}`
                              }
                              size="small"
                              onDelete={() =>
                                setTempDemoCourse(
                                  tempDemoCourse.filter(
                                    (id) =>
                                      id !==
                                      value
                                  )
                                )
                              }
                              deleteIcon={
                                <CloseIcon />
                              }
                              sx={{
                                backgroundColor:
                                  theme
                                    .palette
                                    .primary
                                    .main,

                                color:
                                  '#fff',
                              }}
                            />
                          );
                        }
                      )}
                    </Box>
                  )}
                  MenuProps={{
                    PaperProps:
                      {
                        sx: {
                          maxHeight:
                            300,
                        },
                      },
                  }}
                >
                  {courseData.map(
                    (course) => (
                      <MenuItem
                        key={
                          course.id
                        }
                        value={
                          course.id
                        }
                      >
                        <Checkbox
                          checked={
                            tempDemoCourse.indexOf(
                              course.id
                            ) > -1
                          }
                        />

                        <Typography>
                          {course.course_name ||
                            course.name ||
                            `Course ${course.id}`}
                        </Typography>
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>

              {/* Demo Batch */}

              <FormControl
                fullWidth
                required
                disabled={
                  tempDemoCourse.length ===
                  0
                }
              >
                <InputLabel
                  shrink={
                    tempDemoBatch.length >
                    0
                  }
                  sx={{
                    color:
                      theme.palette
                        .text
                        .primary,

                    '&.Mui-focused':
                      {
                        color:
                          theme.palette
                            .primary
                            .main,
                      },
                  }}
                >
                  Batch *
                </InputLabel>

                <Select
                  multiple
                  value={
                    tempDemoBatch
                  }
                  label="Batch *"
                  onChange={(e) => {
                    const {
                      value,
                    } =
                      e.target;

                    setTempDemoBatch(
                      typeof value ===
                        'string'
                        ? value.split(
                            ','
                          )
                        : value
                    );
                  }}
                  renderValue={(
                    selected
                  ) => (
                    <Box
                      sx={{
                        display:
                          'flex',
                        flexWrap:
                          'wrap',
                        gap: 0.5,
                      }}
                    >
                      {selected.map(
                        (value) => {
                          const batch =
                            batchData.find(
                              (b) =>
                                b.id ===
                                value
                            );

                          const courseName =
                            getCourseName(
                              batch?.course,
                              courseData
                            );

                          return (
                            <Tooltip
                              key={
                                value
                              }
                              title={`Course: ${courseName}`}
                              arrow
                            >
                              <Chip
                                label={
                                  batch?.batch_name ||
                                  batch?.name ||
                                  `Batch ${value}`
                                }
                                size="small"
                                icon={
                                  <span>
                                    📚
                                  </span>
                                }
                                onDelete={() =>
                                  setTempDemoBatch(
                                    tempDemoBatch.filter(
                                      (id) =>
                                        id !==
                                        value
                                    )
                                  )
                                }
                                deleteIcon={
                                  <CloseIcon />
                                }
                                sx={{
                                  backgroundColor:
                                    theme
                                      .palette
                                      .secondary
                                      .main,

                                  color:
                                    '#fff',
                                }}
                              />
                            </Tooltip>
                          );
                        }
                      )}
                    </Box>
                  )}
                  MenuProps={{
                    PaperProps:
                      {
                        sx: {
                          maxHeight:
                            400,
                        },
                      },
                  }}
                >
                  {Object.entries(
                    getDemoBatchesGroupedByCourse()
                  ).map(
                    ([
                      courseName,
                      courseBatches,
                    ]) => (
                      <Fragment
                        key={
                          courseName
                        }
                      >
                        <ListSubheader
                          sx={{
                            backgroundColor:
                              theme
                                .palette
                                .primary
                                .main +
                              '10',

                            color:
                              theme
                                .palette
                                .primary
                                .main,

                            fontWeight:
                              'bold',

                            lineHeight:
                              '36px',
                          }}
                        >
                          🎓{' '}
                          {
                            courseName
                          }
                        </ListSubheader>

                        {courseBatches.map(
                          (batch) => (
                            <MenuItem
                              key={
                                batch.id
                              }
                              value={
                                batch.id
                              }
                            >
                              <Checkbox
                                checked={
                                  tempDemoBatch.indexOf(
                                    batch.id
                                  ) >
                                  -1
                                }
                                onChange={(
                                  e
                                ) => {
                                  e.stopPropagation();

                                  const index =
                                    tempDemoBatch.indexOf(
                                      batch.id
                                    );

                                  const next =
                                    [
                                      ...tempDemoBatch,
                                    ];

                                  if (
                                    index ===
                                    -1
                                  ) {
                                    next.push(
                                      batch.id
                                    );
                                  } else {
                                    next.splice(
                                      index,
                                      1
                                    );
                                  }

                                  setTempDemoBatch(
                                    next
                                  );
                                }}
                              />

                              <Box
                                sx={{
                                  display:
                                    'flex',

                                  flexDirection:
                                    'column',

                                  ml: 1,
                                }}
                              >
                                <Typography>
                                  {batch.batch_name ||
                                    batch.name ||
                                    `Batch ${batch.id}`}
                                </Typography>

                                {batch.batch_code && (
                                  <Typography
                                    variant="caption"
                                  >
                                    Code:{' '}
                                    {
                                      batch.batch_code
                                    }
                                  </Typography>
                                )}
                              </Box>
                            </MenuItem>
                          )
                        )}
                      </Fragment>
                    )
                  )}
                </Select>
              </FormControl>

              {/* Demo Faculty */}

              <FormControl
                fullWidth
                required
                disabled={
                  tempDemoCourse.length ===
                    0 ||
                  tempDemoBatch.length ===
                    0
                }
              >
                <InputLabel
                  sx={{
                    color:
                      theme.palette
                        .text
                        .primary,

                    '&.Mui-focused':
                      {
                        color:
                          theme.palette
                            .primary
                            .main,
                      },
                  }}
                >
                  Faculty *
                </InputLabel>

                <Select
                  multiple
                  value={
                    formData.demoFaculty
                  }
                  label="Faculty *"
                  onChange={(e) => {
                    const {
                      value,
                    } =
                      e.target;

                    setFormData(
                      (prev) => ({
                        ...prev,
                        demoFaculty:
                          typeof value ===
                          'string'
                            ? value.split(
                                ','
                              )
                            : value,
                      })
                    );
                  }}
                  renderValue={(
                    selected
                  ) => (
                    <Box
                      sx={{
                        display:
                          'flex',
                        flexWrap:
                          'wrap',
                        gap: 0.5,
                      }}
                    >
                      {selected.map(
                        (facultyId) => {
                          const faculty =
                            facultyData.find(
                              (f) =>
                                f.id ===
                                parseInt(
                                  facultyId
                                )
                            );

                          return (
                            <Chip
                              key={
                                facultyId
                              }
                              label={
                                faculty?.name ||
                                `${faculty?.first_name || ''} ${
                                  faculty?.last_name || ''
                                }`.trim() ||
                                `Faculty ${facultyId}`
                              }
                              size="small"
                              onDelete={() =>
                                setFormData(
                                  (
                                    prev
                                  ) => ({
                                    ...prev,
                                    demoFaculty:
                                      prev.demoFaculty.filter(
                                        (
                                          id
                                        ) =>
                                          id !==
                                          facultyId
                                      ),
                                  })
                                )
                              }
                              deleteIcon={
                                <CloseIcon />
                              }
                              sx={{
                                backgroundColor:
                                  theme
                                    .palette
                                    .primary
                                    .main,

                                color:
                                  '#fff',
                              }}
                            />
                          );
                        }
                      )}
                    </Box>
                  )}
                  MenuProps={{
                    PaperProps:
                      {
                        sx: {
                          maxHeight:
                            300,
                        },
                      },
                  }}
                >
                  {filteredDemoFaculty.length ===
                  0 ? (
                    <MenuItem
                      disabled
                    >
                      No faculty available
                    </MenuItem>
                  ) : (
                    filteredDemoFaculty.map(
                      (
                        faculty
                      ) => (
                        <MenuItem
                          key={
                            faculty.id
                          }
                          value={
                            faculty.id
                          }
                        >
                          <Checkbox
                            checked={
                              formData.demoFaculty.indexOf(
                                faculty.id
                              ) >
                              -1
                            }
                          />

                          <Typography>
                            {faculty.name ||
                              `${faculty.first_name || ''} ${
                                faculty.last_name || ''
                              }`.trim() ||
                              `Faculty ${faculty.id}`}
                          </Typography>
                        </MenuItem>
                      )
                    )
                  )}
                </Select>
              </FormControl>

              {/* Demo Date */}

              <TextField
                label="Demo Date"
                type="date"
                fullWidth
                name="demoDate"
                value={
                  formData.demoDate
                }
                onChange={
                  handleChange
                }
                InputLabelProps={{
                  shrink: true,
                }}
                sx={fieldStyle}
              />

              {/* Demo Time */}

              <TextField
                label="Demo Time"
                type="time"
                fullWidth
                name="demoTime"
                value={
                  formData.demoTime
                }
                onChange={
                  handleChange
                }
                InputLabelProps={{
                  shrink: true,
                }}
                sx={fieldStyle}
              />
            </Box>
          </Box>

          {/* Footer */}

          <Box
            sx={{
              display:
                'flex',
              justifyContent:
                'flex-end',
              gap: 2,
              p: 2,
              borderTop:
                `1px solid ${theme.palette.divider}`,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                setShowDemoModal(
                  false
                );

                setTempDemoCourse(
                  []
                );

                setTempDemoBatch(
                  []
                );
              }}
             data-app-cancel="true">
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={
                handleSaveDemoDetails
              }
              disabled={
                tempDemoCourse.length ===
                  0 ||
                tempDemoBatch.length ===
                  0 ||
                !formData.demoFaculty
                  .length ||
                !formData.demoDate ||
                !formData.demoTime
              }
            >
              Apply Demo Details
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* ======================================================
          SUCCESS MODAL
      ====================================================== */}

      <Modal
        open={
          showSuccessModal
        }
        onClose={() =>
          setShowSuccessModal(
            false
          )
        }
        BackdropProps={{
          style: {
            backgroundColor:
              'rgba(0, 0, 0, 0.8)',
          },
        }}
      >
        <Box
          sx={{
            position:
              'absolute',

            top: '50%',
            left: '50%',

            transform:
              'translate(-50%, -50%)',

            width: 400,

            bgcolor:
              theme.palette
                .background
                .paper,

            borderRadius: 2,

            boxShadow: 24,

            overflow:
              'hidden',
          }}
        >
          <Box
            sx={{
              backgroundColor:
                theme.palette
                  .primary
                  .main,

              padding:
                '12px 16px',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color:
                  theme
                    .palette
                    .primary
                    .contrastText,

                fontWeight:
                  'bold',
              }}
            >
              Enquiry Added ✅
            </Typography>
          </Box>

          <Box
            sx={{
              padding:
                '20px',
            }}
          >
            <Typography
              sx={{
                mb: 2,

                color:
                  theme.palette
                    .text
                    .primary,
              }}
            >
              Enquiry Added
              Successfully
            </Typography>

            <Box
              sx={{
                display:
                  'flex',
                justifyContent:
                  'flex-end',
              }}
            >
              <Button
                onClick={() => {
                  setShowSuccessModal(
                    false
                  );

                  navigate(-1);
                }}
                variant="contained"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* ======================================================
          SNACKBAR
      ====================================================== */}

      <Snackbar
        open={
          openSnackbar
        }
        autoHideDuration={
          4000
        }
        onClose={() =>
          setOpenSnackbar(
            false
          )
        }
        anchorOrigin={{
          vertical: 'top',
          horizontal:
            'center',
        }}
      >
        <Alert
          onClose={() =>
            setOpenSnackbar(
              false
            )
          }
          severity={
            snackbarSeverity
          }
          sx={{
            width: '100%',
          }}
        >
          {
            snackbarMessage
          }
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddEnquiry;